import { DateTime } from 'luxon';
import dayjs from '@/core/boot/day';
import { useAppStore } from '@/core/stores/app.store';

// Parse API-format calendar fields in UTC before applying the patient's zone.
const LOCAL_FORMATS = Object.freeze([
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss.SSS',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm',
  'YYYY-MM-DD',
]);

function formatCalendar(date, format, locale = dayjs.locale()) {
  // Day.js only formats API tokens; instants and arithmetic stay in Luxon.
  return dayjs
    .utc(date.toISO({ includeOffset: false }))
    .locale(locale)
    .utcOffset(date.offset, true)
    .format(format);
}

class DateUtils {
  static SERVER_DATE_FORMAT = 'YYYY-MM-DD';
  static SERVER_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

  static getClientDateTimeConfig() {
    return useAppStore().getDateTimeConfig;
  }

  static getClientTimezone() {
    return this.getClientDateTimeConfig().timezone;
  }

  static getCurrentUserTimeZoneTime(format = '') {
    const date = DateTime.now().setZone(this.getClientTimezone()).setLocale(dayjs.locale());
    if (format) {
      return formatCalendar(date, format);
    }
    return date;
  }

  // Unzoned strings are patient-local fields. Date/DateTime/Day.js objects and
  // explicit ISO offsets are instants: preserve their epoch milliseconds.
  static parseUserDate(value, format = null) {
    const zone = this.getClientTimezone();
    let date;
    if (DateTime.isDateTime(value)) {
      date = value.setZone(zone);
    } else if (value instanceof Date || dayjs.isDayjs(value)) {
      date = DateTime.fromMillis(value.valueOf(), { zone, locale: dayjs.locale() });
    } else if (typeof value === 'string' && value) {
      if (!format && /T.*(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
        date = DateTime.fromISO(value, { zone, locale: dayjs.locale() });
      } else {
        const sourceFormat =
          format || LOCAL_FORMATS.find((candidate) => dayjs.utc(value, candidate, true).isValid());
        if (!sourceFormat) {
          return null;
        }
        const fields = dayjs.utc(value, sourceFormat, true);
        if (!fields.isValid()) {
          return null;
        }
        date = DateTime.fromObject(
          {
            year: fields.year(),
            month: fields.month() + 1,
            day: fields.date(),
            hour: fields.hour(),
            minute: fields.minute(),
            second: fields.second(),
            millisecond: fields.millisecond(),
          },
          { zone, locale: dayjs.locale() },
        );
        // Reject absent patient-local times instead of shifting them across a DST gap.
        if (
          date.day !== fields.date() ||
          (/[Hh]/.test(sourceFormat.replace(/\[[^\]]*\]/g, '')) &&
            (date.hour !== fields.hour() || date.minute !== fields.minute()))
        ) {
          return null;
        }
      }
    }
    if (!date?.isValid) {
      return null;
    }
    return date;
  }

  static isValidDateTime(value) {
    return this.parseUserDate(value) !== null;
  }

  static isToday(value) {
    const date = this.parseUserDate(value);
    return Boolean(date?.hasSame(this.getCurrentUserTimeZoneTime(), 'day'));
  }

  static formatDateTime(value, format = '', sourceFormat = null, locale = dayjs.locale()) {
    const date = this.parseUserDate(value, sourceFormat);
    if (!date) {
      return '';
    }
    return formatCalendar(date, format || this.getClientDateTimeConfig().full, locale);
  }

  static formatDate(value, sourceFormat = null) {
    return this.formatDateTime(value, this.getClientDateTimeConfig().date, sourceFormat);
  }

  // A clock is not an instant: preserve its fields without applying any timezone.
  static formatTime(time) {
    const date = dayjs.utc(time, 'HH:mm', true);
    if (!date.isValid()) {
      return '';
    }
    return date.format(this.getClientDateTimeConfig().hourMinute);
  }

  static formatWeekdayDate(value, sourceFormat = null) {
    const date = this.parseUserDate(value, sourceFormat);
    if (!date) {
      return '';
    }
    return formatCalendar(date, 'ddd, ' + this.getClientDateTimeConfig().dayMonthShort);
  }

  static formatForServer(value, sourceFormat = null, targetFormat = null) {
    return this.formatDateTime(value, targetFormat || this.SERVER_DATETIME_FORMAT, sourceFormat);
  }

  // Raw datetimes are UTC; date-only/clock-only values remain patient-local fields.
  static parseServerDate(value, format = null) {
    if (typeof value === 'string' && (format || /^\d{4}-\d{2}-\d{2}$/.test(value))) {
      return this.parseUserDate(value, format || this.SERVER_DATE_FORMAT);
    }
    if (typeof value !== 'string') {
      return this.parseUserDate(value);
    }
    let date;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      date = DateTime.fromISO(value, { zone: 'UTC' });
    } else {
      const fields = dayjs.utc(value, this.SERVER_DATETIME_FORMAT, true);
      if (!fields.isValid()) {
        return null;
      }
      date = DateTime.fromMillis(fields.valueOf(), { zone: 'UTC' });
    }
    if (!date.isValid) {
      return null;
    }
    return date.setZone(this.getClientTimezone()).setLocale(dayjs.locale());
  }

  static formatServerDateTime(value, format = '', locale = dayjs.locale()) {
    const date = this.parseServerDate(value);
    if (!date) {
      return '';
    }
    return formatCalendar(date, format || this.getClientDateTimeConfig().full, locale);
  }

  // Compact datetime label in the patient's configured date and clock formats.
  static formatServerDayTime(value) {
    const config = this.getClientDateTimeConfig();
    return this.formatServerDateTime(value, `${config.dayMonthShort} [·] ${config.hourMinute}`);
  }
}

export default DateUtils;
